import {
  tuitionCourseLabels,
  type TuitionSelection,
} from './tuitionSubjects/catalog.js';
/**
 * Tuition pricing constants and helpers shared by public payment UI and server
 * payment record creation.
 */

export const TUITION_LESSONS_PER_PAYMENT_CYCLE = 4;

/**
 * How many whole cycles one bill may ask for. Bills are composed in whole
 * cycles only: 1 is the frozen default every surface assumes, and 2 or 3 are
 * the composer's "8 lessons" / "12 lessons" overrides. The same cap is enforced
 * at the compose boundary, on the payment-link token and in the token helper,
 * so a multiplier can enter the money path from nowhere but an admin's own
 * choice — never from a parent-facing request.
 */
export const MAX_CYCLES_PER_BILL = 3;
export const BILL_CYCLE_OPTIONS = [1, 2, 3] as const;
export type BillCycles = (typeof BILL_CYCLE_OPTIONS)[number];

/** True for a whole number of cycles a bill may ask for (1..MAX). */
export function isBillCycles(value: unknown): value is BillCycles {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_CYCLES_PER_BILL
  );
}

/** Lessons a bill for `cycles` whole cycles covers: 4, 8, 12. */
export function lessonsForCycles(cycles: number): number {
  return cycles * TUITION_LESSONS_PER_PAYMENT_CYCLE;
}

export const LEGACY_TUITION_PRICE_PER_LESSON = 30;
export const CURRENT_TUITION_PRICE_PER_LESSON = 50;
export const PRIVATE_TUITION_PRICE_PER_LESSON = 100;
export const TEACHING_FORMATS = ['group', 'private'] as const;
export type TeachingFormat = (typeof TEACHING_FORMATS)[number];

/** Existing classes are groups regardless of roster size. */
export function resolveTeachingFormat(value?: string | null): TeachingFormat {
  return value === 'private' ? 'private' : 'group';
}

export function getClassLessonAmount(
  teachingFormat: string | null | undefined,
  enrollment: EnrollmentPricingInput = {}
): number {
  return resolveTeachingFormat(teachingFormat) === 'private'
    ? PRIVATE_TUITION_PRICE_PER_LESSON
    : getTuitionLessonAmount(resolveTuitionPricingTier(enrollment));
}

export function teachingFormatLabel(format?: string | null): string {
  return resolveTeachingFormat(format) === 'private'
    ? 'Private one-on-one'
    : 'Group';
}

export const LEGACY_TUITION_CYCLE_AMOUNT =
  LEGACY_TUITION_PRICE_PER_LESSON * TUITION_LESSONS_PER_PAYMENT_CYCLE;
export const CURRENT_TUITION_CYCLE_AMOUNT =
  CURRENT_TUITION_PRICE_PER_LESSON * TUITION_LESSONS_PER_PAYMENT_CYCLE;

export const TUITION_PRICING_TIERS = ['legacy', 'current'] as const;
export type TuitionPricingTier = (typeof TUITION_PRICING_TIERS)[number];

export interface EnrollmentPricingInput extends TuitionSelection {
  readonly tuitionPricingTier?: TuitionPricingTier | string | null;
  readonly subjects?: readonly string[] | null;
}

export function resolveTuitionPricingTier(
  enrollment: EnrollmentPricingInput
): TuitionPricingTier {
  return enrollment.tuitionPricingTier === 'legacy' ? 'legacy' : 'current';
}

/** What ONE lesson costs at `tier`. A cycle is four of these. */
export function getTuitionLessonAmount(tier: TuitionPricingTier): number {
  return tier === 'legacy'
    ? LEGACY_TUITION_PRICE_PER_LESSON
    : CURRENT_TUITION_PRICE_PER_LESSON;
}

export function getTuitionCycleAmount(
  tier: TuitionPricingTier,
  subjectMultiplier = 1
): number {
  const baseAmount =
    tier === 'legacy'
      ? LEGACY_TUITION_CYCLE_AMOUNT
      : CURRENT_TUITION_CYCLE_AMOUNT;
  return baseAmount * subjectMultiplier;
}

export function getMathSubjectPaymentMultiplier(
  subjects?: readonly string[] | null
): number {
  const normalizedSubjects = new Set(
    (subjects ?? []).map(subject => subject.trim().toLowerCase())
  );
  return normalizedSubjects.has('e-math') && normalizedSubjects.has('a-math')
    ? 2
    : 1;
}

/**
 * What the PayNow landing page asks a parent for: one cycle at the enrollment's
 * tier, doubled for E-Math + A-Math, times the whole cycles the link was minted
 * for (`billCycles`, default 1). Multiplied in cents so a legacy $120 tier × 3
 * can never drift by a fraction of a cent.
 */
export function resolveEnrollmentPaymentAmount(
  enrollment: EnrollmentPricingInput,
  billCycles = 1
): number {
  const base = getTuitionCycleAmount(
    resolveTuitionPricingTier(enrollment),
    tuitionCourseLabels(enrollment).length
  );
  return (Math.round(base * 100) * billCycles) / 100;
}
